import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CrudForm, CrudStyles } from '../shared/Crud';
import AddressForm from '../shared/AddressForm';
import PhoneNumbersInput from '../shared/PhoneNumbersInput';
import { useForm } from '../../hooks/useForm';
import physicalPersonService from '../../services/physicalPersonService';
import { buildAddressDtoFromForm } from '../../utils/legalPersonMappers';
import { maskCPF } from '../../utils/masks';
import { isValidCPF, isValidEmail, isValidZipCode } from '../../utils/validation';

const { FormGroup, FormRow, Input, SectionTitle } = CrudStyles;

const initialFormData = {
    name: '',
    email: '',
    cpf: '',
};

function collectValidPhones(phones) {
    return (phones || [])
        .map((p) => String(p || '').trim())
        .filter((p) => p.length > 0)
        .filter((p) => {
            const d = p.replace(/\D/g, '');
            return d.length >= 10 && d.length <= 11;
        });
}

export default function PhysicalPersonForm({ onCancel, onSuccess }) {
    const addressRef = useRef({});
    const [phones, setPhones] = useState(['']);
    const phonesRef = useRef(phones);

    useEffect(() => {
        phonesRef.current = phones;
    }, [phones]);

    const validateForm = useCallback((data) => {
        const err = {};
        const addr = addressRef.current;

        if (!data.name?.trim()) err.name = 'Nome é obrigatório';
        else if (data.name.trim().length < 2) err.name = 'Nome deve ter ao menos 2 caracteres';

        if (!data.email?.trim()) err.email = 'E-mail é obrigatório';
        else if (!isValidEmail(data.email)) err.email = 'E-mail inválido';

        const validPhones = collectValidPhones(phonesRef.current);
        if (validPhones.length === 0) {
            err.phones = 'Informe ao menos um telefone válido';
        }

        const cpfDigits = (data.cpf || '').replace(/\D/g, '');
        if (cpfDigits && !isValidCPF(cpfDigits)) err.cpf = 'CPF inválido';

        if (!addr.zipCode?.trim()) err.zipCode = 'CEP é obrigatório';
        else if (!isValidZipCode(addr.zipCode)) err.zipCode = 'CEP inválido';
        if (!addr.street?.trim()) err.street = 'Logradouro é obrigatório';
        if (!addr.number?.trim()) err.number = 'Número é obrigatório';
        if (!addr.district?.trim()) err.district = 'Bairro é obrigatório';
        if (!addr.city?.trim()) err.city = 'Cidade é obrigatória';
        if (!addr.stateAbbr?.trim()) err.stateAbbr = 'UF é obrigatório';

        return err;
    }, []);

    const handleApiSubmit = useCallback(
        async (data) => {
            const cpfDigits = (data.cpf || '').replace(/\D/g, '');
            const payload = {
                name: data.name.trim(),
                email: data.email.trim(),
                phoneNumbers: collectValidPhones(phonesRef.current).map((p) => ({ number: p })),
                cpf: cpfDigits.length === 11 ? maskCPF(cpfDigits) : null,
                address: buildAddressDtoFromForm(addressRef.current),
            };
            await physicalPersonService.create(payload);
            onSuccess?.();
        },
        [onSuccess],
    );

    const { formData, errors, handleChange, handleSubmit, isSubmitting } = useForm(
        initialFormData,
        handleApiSubmit,
        validateForm,
    );

    const handleCpfChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
        handleChange({
            target: { name: 'cpf', value: digits.length ? maskCPF(digits) : '' },
        });
    };

    return (
        <CrudForm
            title="Nova pessoa física"
            onSubmit={handleSubmit}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
        >
            <SectionTitle>Dados pessoais</SectionTitle>
            <FormRow>
                <FormGroup>
                    <label htmlFor="pf-name">Nome</label>
                    <Input
                        id="pf-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        $hasError={!!errors.name}
                        maxLength={100}
                    />
                    {errors.name && <span className="error">{errors.name}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="pf-email">E-mail</label>
                    <Input
                        id="pf-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        $hasError={!!errors.email}
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </FormGroup>
            </FormRow>
            <FormRow>
                <FormGroup>
                    <label htmlFor="pf-cpf">CPF (opcional)</label>
                    <Input
                        id="pf-cpf"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleCpfChange}
                        $hasError={!!errors.cpf}
                        placeholder="000.000.000-00"
                        maxLength={14}
                    />
                    {errors.cpf && <span className="error">{errors.cpf}</span>}
                </FormGroup>
            </FormRow>

            <SectionTitle>Telefones</SectionTitle>
            <PhoneNumbersInput
                label="Telefones *"
                initialPhones={phones}
                onChange={(next) => {
                    setPhones(next);
                }}
            />
            {errors.phones && (
                <p className="error" style={{ margin: '8px 0 0' }}>
                    {errors.phones}
                </p>
            )}

            <SectionTitle>Endereço</SectionTitle>
            <AddressForm
                onChange={(addr) => {
                    addressRef.current = addr;
                }}
                errors={{
                    zipCode: errors.zipCode,
                    street: errors.street,
                    number: errors.number,
                    complement: errors.complement,
                    district: errors.district,
                    city: errors.city,
                    stateAbbr: errors.stateAbbr,
                }}
                customerId={null}
            />

            {errors.submit && (
                <p className="error" style={{ margin: 0, whiteSpace: 'pre-line' }}>
                    {errors.submit}
                </p>
            )}
        </CrudForm>
    );
}
